/**
 * Size and place of the ticket submission panel (#1170). It floats like the
 * Notification Center (#1129/#1133): same inset, a fixed height while open
 * (attachments and the marking view scroll inside), maximise to the full
 * available height.
 *
 * Two anchors: `taskbar-right` in the desktop, bottom right above the
 * taskbar; `rail-left` under the shell-free routes, bottom left beside the
 * 56 px icon rail, where there is no taskbar. Anchor type and rail width are
 * the Notification Center's (`notifications/panelSize.ts`, #1169); only the
 * size differs.
 */
import type React from 'react'
import { TASKBAR_HEIGHT } from '@/shared/ui'
import {
  PANEL_INSET_PX,
  PANEL_MAXIMIZED_WIDTH_PX,
  STANDALONE_RAIL_WIDTH_PX,
  type PanelAnchor,
} from '../notifications/panelSize'

export type TicketPanelAnchor = PanelAnchor

/** A little wider and taller than the Notification Center: a form with attachments. */
export const TICKET_PANEL_WIDTH_PX = 440
export const TICKET_PANEL_MAX_HEIGHT_PX = 760

export const TICKET_PANEL_MAXIMIZED_STORAGE_KEY = 'cdb-ticket-panel-maximized'

export function ticketPanelGeometry(maximized: boolean, anchor: TicketPanelAnchor): React.CSSProperties {
  const bottomReserved = anchor === 'taskbar-right' ? TASKBAR_HEIGHT : 0
  const leftReserved = anchor === 'rail-left' ? STANDALONE_RAIL_WIDTH_PX : 0
  const availableHeight = `calc(100vh - ${bottomReserved + 2 * PANEL_INSET_PX}px)`
  const availableWidth = `calc(100vw - ${leftReserved + 2 * PANEL_INSET_PX}px)`
  return {
    ...(anchor === 'rail-left' ? { left: `${leftReserved + PANEL_INSET_PX}px` } : { right: `${PANEL_INSET_PX}px` }),
    bottom: `${bottomReserved + PANEL_INSET_PX}px`,
    width: `min(${maximized ? PANEL_MAXIMIZED_WIDTH_PX : TICKET_PANEL_WIDTH_PX}px, ${availableWidth})`,
    height: maximized ? availableHeight : `min(${TICKET_PANEL_MAX_HEIGHT_PX}px, ${availableHeight})`,
  }
}

/** Remembered per browser, like the Notification Center's choice; unreadable storage means normal size. */
export function readTicketPanelMaximized(): boolean {
  try {
    return localStorage.getItem(TICKET_PANEL_MAXIMIZED_STORAGE_KEY) === 'true'
  } catch {
    // Blocked site data throws on access; the normal size is always safe.
    return false
  }
}

export function storeTicketPanelMaximized(maximized: boolean): void {
  try {
    localStorage.setItem(TICKET_PANEL_MAXIMIZED_STORAGE_KEY, String(maximized))
  } catch {
    // The choice still applies for this session.
  }
}
